import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogoMark } from '../components/Logo';
import api from '../utils/api';

export default function ChangePassword() {
  const { user, setUserData, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next.length < 8) return setError('New password must be at least 8 characters');
    if (next !== confirm) return setError('Passwords do not match');
    if (next === current) return setError('New password must be different from the current one');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/change-password', { currentPassword: current, newPassword: next });
      setUserData(data.user);
      navigate('/app', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes slideUp{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <div style={{ width: 420, animation: 'slideUp 0.5s ease' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', border: '1px solid var(--brd)', background: 'rgba(0,212,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', position: 'relative' }}>
            <div style={{ position: 'absolute', inset: -6, borderRadius: '50%', border: '1px solid transparent', borderTopColor: 'var(--j1)', animation: 'spin 4s linear infinite' }} />
            <LogoMark size={38} />
          </div>
          <div style={{ fontFamily: 'Orbitron', fontSize: 20, fontWeight: 700, color: 'var(--txt)', letterSpacing: 3 }}>NEURALOPS</div>
        </div>

        <div className="card" style={{ padding: 28 }}>
          {/* Lock icon + heading */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🔐</div>
            <div style={{ fontFamily: 'Orbitron', fontSize: 14, fontWeight: 700, color: 'var(--j1)', letterSpacing: 2 }}>PASSWORD CHANGE REQUIRED</div>
            <div style={{ fontSize: 12, color: 'var(--txt2)', marginTop: 8, lineHeight: 1.6 }}>
              Your account was created with a temporary password.<br />
              You must set a personal password before continuing.
            </div>
          </div>

          {/* Who is logged in */}
          <div style={{ padding: '10px 14px', background: 'rgba(0,212,255,0.04)', border: '1px solid var(--brd2)', borderRadius: 4, marginBottom: 20, fontSize: 12, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>
            Logged in as <span style={{ color: 'var(--j1)' }}>{user?.name}</span> &nbsp;·&nbsp; <span style={{ color: 'var(--txt3)' }}>{user?.email}</span>
          </div>

          <form onSubmit={submit}>
            <div className="form-group">
              <label className="label">Temporary / Current Password</label>
              <input className="input" type="password" value={current} onChange={e => setCurrent(e.target.value)} placeholder="Enter the password you received" required autoFocus />
            </div>
            <div className="form-group">
              <label className="label">New Password <span style={{ color: 'var(--txt3)', fontWeight: 400 }}>(min 8 characters)</span></label>
              <input className="input" type="password" value={next} onChange={e => setNext(e.target.value)} placeholder="Choose a strong password" required />
            </div>
            <div className="form-group">
              <label className="label">Confirm New Password</label>
              <input className="input" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat new password" required />
            </div>

            {/* Strength hint */}
            {next.length > 0 && (
              <div style={{ marginBottom: 14, display: 'flex', gap: 4 }}>
                {[8, 12, 16].map((len, i) => (
                  <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: next.length >= len ? ['var(--j6)', 'var(--j4)', 'var(--j5)'][i] : 'var(--brd2)', transition: 'background 0.3s' }} />
                ))}
                <span style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', marginLeft: 6, lineHeight: '12px' }}>
                  {next.length < 8 ? 'WEAK' : next.length < 12 ? 'OK' : next.length < 16 ? 'GOOD' : 'STRONG'}
                </span>
              </div>
            )}

            {error && (
              <div style={{ color: 'var(--j6)', fontSize: 11, fontFamily: 'Share Tech Mono', marginBottom: 14, padding: '8px 12px', background: 'rgba(255,68,68,0.06)', border: '1px solid rgba(255,68,68,0.2)', borderRadius: 4 }}>
                {error}
              </div>
            )}

            <button className="btn btn-primary" type="submit" disabled={loading} style={{ width: '100%', padding: 13, fontSize: 12, letterSpacing: 3 }}>
              {loading ? 'UPDATING...' : 'SET NEW PASSWORD'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button onClick={logout} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 1 }}>
              SIGN OUT
            </button>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 1 }}>
          NEURALOPS COE · WISSEN TECHNOLOGY · INTERNAL CONFIDENTIAL
        </div>
      </div>
    </div>
  );
}
